import { useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { CircularProgress, TextField } from '@mui/material';
import { getContacts } from '../../pages/contacts/api-service';
import { formatContactName } from '../../pages/contacts/format-contact-name';

const SEARCH_DEBOUNCE_MS = 300;

function getOptionLabel(option) {
  return option ? formatContactName(option) : '';
}

function ContactAutocomplete({ value, onChange, label = 'Contact', disabled = false }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState(value ? [value] : []);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (value && !options.some(option => option.id === value.id)) {
      setOptions(prev => [value, ...prev]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      getContacts({ name: inputValue, limit: 20 })
        .then((result) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          setOptions(result.data || []);
        })
        .catch(() => {
          if (requestId === requestIdRef.current) {
            setOptions([]);
          }
        })
        .finally(() => {
          if (requestId === requestIdRef.current) {
            setLoading(false);
          }
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [inputValue]);

  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      value={value || null}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(event, newValue) => onChange(newValue)}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default ContactAutocomplete;
